import {
  createUserWithEmailAndPassword,
  sendEmailVerification as firebaseSendEmailVerification,
  User as FirebaseUser,
  UserCredential,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {
  FirestoreCollection,
  ScalekitCustomProperties,
  User,
} from 'hubbl-shared';
import {UserDTO} from '../models';
import {firebaseAuth, firebaseFunctions, firestore} from './firebase';
import {mapUserDTOToUser} from './utils/map-incoming-users/mapUserDTOToUser';

const getUserDetails = async (
  companyId: string,
  authUserId: string,
): Promise<User | null> => {
  const userPromise = new Promise<User | null>(async (resolve, reject) => {
    try {
      const companyRef = doc(
        firestore,
        FirestoreCollection.Companies,
        companyId,
      );

      const userRef = doc(companyRef, FirestoreCollection.Users, authUserId);
      const userSnapshot = await getDoc(userRef);

      if (userSnapshot.exists()) {
        //TODO: PARSE CREATED AT LIKE IN APP
        resolve(mapUserDTOToUser(userSnapshot.data() as UserDTO));
      } else {
        resolve(null);
      }
    } catch (error) {
      reject(error);
    }
  });
  return userPromise;
};

const updateUserDetails = async (
  companyId: string,
  userUid: string,
  userData: Partial<User>,
) => {
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const userRef = doc(companyRef, FirestoreCollection.Users, userUid);
  await updateDoc(userRef, userData);
};

const completeCreateProfileFlow = async (
  companyId: string,
  userUid: string,
  userData: Partial<User>,
) => {
  const batch = writeBatch(firestore);

  const userRef = doc(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Users,
    userUid,
  );
  batch.update(userRef, {
    ...userData,
    isOnboarded: true,
  });

  await batch.commit();
};

const onLogIn = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  return signInWithEmailAndPassword(firebaseAuth, email, password)
    .then(authUser => {
      return authUser;
    })
    .catch(error => {
      const errorCode = error.code;
      const errorMessage = error.message;
      throw error(errorCode, errorMessage);
    });
};

const onLogOut = async () => {
  return await firebaseAuth.signOut();
};

const signUpWithEmail = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  return createUserWithEmailAndPassword(firebaseAuth, email, password);
};

const sendEmailVerification = async (user: FirebaseUser): Promise<void> => {
  await firebaseSendEmailVerification(user);
};

const onMoveUnattachedUserToCompany = async (companyPincode: string) => {
  const callableFunc = httpsCallable(
    firebaseFunctions,
    //TODO: Add to constant in shared
    'onMoveUnattachedUserToCompany',
  );
  return callableFunc({
    companyPincode,
  })
    .then(result => {
      console.log(result.data);
      const data = result.data;
      return data;
    })
    .catch(error => {
      console.log(`error: ${JSON.stringify(error)}`);
      throw error;
    });
};

const onCheckScalekitConnection = async (email: string) => {
  const checkConnection = httpsCallable<
    {email: string},
    {hasConnection: boolean}
  >(firebaseFunctions, 'onCheckScalekitConnection');

  return await checkConnection({email});
};

interface onSyncAndMoveOIDCAuthUserProps {
  photoURL: string;
  firstName: string;
  lastName: string;
  providerUserId: string;
  customProperties: ScalekitCustomProperties;
}
const onSyncAndMoveOIDCAuthUser = async ({
  photoURL,
  firstName,
  lastName,
  providerUserId,
  customProperties,
}: onSyncAndMoveOIDCAuthUserProps) => {
  const callableFunc = httpsCallable(
    firebaseFunctions,
    //TODO: Add to constant in shared
    'onSyncAndMoveOIDCAuthUser',
  );
  return callableFunc({
    photoURL,
    firstName,
    lastName,
    providerUserId,
    customProperties,
  });
};

const getUsersCount = async (companyId: string): Promise<number> => {
  const usersRef = collection(
    firestore,
    FirestoreCollection.Companies,
    companyId,
    FirestoreCollection.Users,
  );

  const usersQuery = query(usersRef, where('email', '!=', 'emil@hubbl.co'));

  const snapshot = await getCountFromServer(usersQuery);
  return snapshot.data().count;
};

const updateLastSignIn = async (companyId: string, userId: string) => {
  if (!companyId || !userId) {
    return;
  }
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const userRef = doc(companyRef, FirestoreCollection.Users, userId);
  await updateDoc(userRef, {
    'stats.lastSignInDate': new Date().toISOString(),
  });
};

const updateUserPlatform = async (companyId: string, userId: string) => {
  if (!companyId || !userId) {
    return;
  }
  const companyRef = doc(firestore, FirestoreCollection.Companies, companyId);
  const userRef = doc(companyRef, FirestoreCollection.Users, userId);
  await updateDoc(userRef, {
    'stats.platform': 'web',
  });
};

export const user = {
  getUserDetails,
  getUsersCount,
  signUpWithEmail,
  sendEmailVerification,
  onLogIn,
  onMoveUnattachedUserToCompany,
  onLogOut,
  onSyncAndMoveOIDCAuthUser,
  onCheckScalekitConnection,
  updateUserDetails,
  completeCreateProfileFlow,
  updateLastSignIn,
  updateUserPlatform,
};
